<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <dialog
    ref="dialogRef"
    :id="id"
    class="custom-dialog"
    @close="$emit('update:visible', false)"
    :class="{ 'slide-in-right': animateFromRight, 'small-right': animateFromRight === 'small', 'medium-right': animateFromRight === 'medium', 'large-right': animateFromRight === 'large' }"
  >
    <div class="dialog-header" v-if="showHeader">
      <slot name="header">{{ header }}</slot>
      <button
        class="close-button"
        aria-label="Close dialog"
        @click="$emit('update:visible', false)"
      >
        ×
      </button>
    </div>
    <div class="dialog-content" v-if="$slots.default">
      <slot></slot>
    </div>
    <div class="dialog-footer" v-if="showFooter">
      <slot name="footer">
        <!-- Uncomment if needed -->
        <!-- <button
          class="dialog-button cancel-button"
          @click="$emit('update:visible', false)"
        >
          Cancel
        </button>
        <button
          class="dialog-button confirm-button"
          @click="$emit('confirm')"
        >
          Confirm
        </button> -->
      </slot>
    </div>
  </dialog>
</template>

<script>
export default {
  props: {
    id: {
      type: String,
      default: "",
    },
    visible: {
      type: Boolean,
      default: false,
    },
    header: {
      type: String,
      default: "",
    },
    position: { // Added for PrimeVue compatibility
      type: String,
      default: "center",
    },
    modal: { // Added for PrimeVue compatibility
      type: Boolean,
      default: true,
    },
    breakpoints: { // Added for PrimeVue compatibility
      type: Object,
      default: () => ({}),
    },
    style: { // Added for PrimeVue compatibility
      type: Object,
      default: () => ({}),
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    showFooter: {
      type: Boolean,
      default: true,
    },
    animateFromRight: {
      type: [String, Boolean],
      default: false,
    },
  },
  emits: ["update:visible", "confirm"],
  methods: {
    handleBackdropClick(event) {
      // Only close on backdrop click if animateFromRight is enabled
      if (this.animateFromRight && event.target === this.$refs.dialogRef) {
        this.$emit('update:visible', false);
      }
    },
  },
  watch: {
    visible(newVal) {
      const dialog = this.$refs.dialogRef;
      if (!dialog) return;

      if (newVal) {
        document.body.classList.add("no-scroll", "dialog-open");
        if (!dialog.open) {
          try {
            dialog.showModal();
          } catch (err) {
            console.warn("Dialog already open:", err);
          }
        }
      } else {
        document.body.classList.remove("no-scroll", "dialog-open");
        if (dialog.open) {
          dialog.close();
        }
      }
    },
  },
  mounted() {
    const dialog = this.$refs.dialogRef;
    if (this.visible && dialog && !dialog.open) {
      try {
        dialog.showModal();
        document.body.classList.add("no-scroll", "dialog-open");
      } catch (err) {
        console.warn("Dialog already open on mount:", err);
      }
    }
    // Add event listener for backdrop click
    if (this.animateFromRight) {
      dialog.addEventListener('click', this.handleBackdropClick);
    }
  },
  beforeUnmount() {
    const dialog = this.$refs.dialogRef;
    document.body.classList.remove("no-scroll", "dialog-open");
    if (dialog && dialog.open) {
      dialog.close();
    }
    // Clean up event listener
    if (this.animateFromRight) {
      dialog.removeEventListener('click', this.handleBackdropClick);
    }
  },
};
</script>

<style lang="scss" scoped>
.custom-dialog {
  border: 1px solid #c0c0bc;
  border-radius: 6px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 90%;
  background-color: #ffffff;
  font-family: "Voyado Text", sans-serif;
  color: #21211f;
  padding: 0;
}

.custom-dialog::backdrop {
  background-color: rgba(0, 0, 0, 0.5); // Added for modal backdrop
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  font-size: 18px;
  font-weight: 500;
  border-bottom: 1px solid #e9ecef;
  background: #f0f0ef;
  color: #2d6e6d;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
}

.close-button:hover {
  color: #21211f;
}

.dialog-content {
  padding: 1rem;
  font-size: 16px;
  line-height: 1.5;
}

.dialog-content:empty {
  padding: 0;
  min-height: 0;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem;
  border-top: 1px solid #fff;
  background-color: #fff;
}
.custom-dialog.slide-in-right {
  height: 100vh;
  max-height: 100vh;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: auto;
  margin: 0;
  border-radius: 0;
  transform: none;
  transition: transform 0.3s ease-in-out;
  overflow-y: auto; /* Allow internal scrolling */
  z-index: 9999;
}

.custom-dialog.small-right {
  min-width: 460px
}

.custom-dialog.medium-right {
  width: 30%; /* Adjusted for medium right dialog */
}

.custom-dialog.large-right {
  width: 50%; /* Added for large right dialog */
}

.custom-dialog.slide-in-right[open] {
  animation: slideInFromRight 0.3s ease-in-out forwards;
}

@keyframes slideInFromRight {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

/* Optional: Mobile full width */
@media (max-width: 768px) {
  .custom-dialog.small-right,
  .custom-dialog.medium-right,
  .custom-dialog.large-right {
    width: 100%;
  }
}
body.no-scroll {
  overflow: hidden;
}

body.dialog-open {
  filter: grayscale(50%) opacity(70%); /* Dim and gray out background */
  pointer-events: none; /* Disable interaction with background */
}

body.dialog-open .custom-dialog::backdrop {
  background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent gray backdrop */
}

body.dialog-open .custom-dialog,
body.dialog-open .custom-dialog * {
  pointer-events: auto; /* Ensure dialog remains interactive */
}
</style>